import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/dialog";
import { Badge } from "@repo/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { Progress } from "@repo/ui/progress";
import { ScrollArea } from "@repo/ui/scroll-area";
import { 
  Loader2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  FileJson, 
  ExternalLink,
  Pause,
  Play,
  Search,
  Database,
  MapPin,
  Plug,
  ChevronDown,
  ChevronRight,
  Info,
  Code,
  Globe,
} from "lucide-react";
import { type Job, type JobRun, fetchJobRuns, pauseJobRun, resumeJobRun, cancelJobRun, fetchJobSuburbs, fetchJobSuburbAdapterBreakdown, fetchJobDiscoveryTasks, fetchJobRunCountsBatch } from "@/lib/api";
import type { JobSuburbWithStats, DiscoveryTaskInfo, JobRunSummary, SuburbBreakdown } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

import { Button } from "@repo/ui/button";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useJobRunCounts } from "@/hooks/useJobRunCounts";
import { describeDedupeSkips } from "@repo/adapter-core/crawl-policy";
import { useAuth } from "@/hooks/useAuth";
import { JobScheduleCard } from "./JobScheduleCard";
import { CADENCE_PRESETS, cadenceForCron } from "./schedulePresets";
import { RunLogsPanel } from "./RunLogsPanel";
import { DiscoveryTasksPanel } from "./DiscoveryTasksPanel";

interface JobDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  /** Called after the schedule card saves, so the caller can refetch the job. */
  onJobUpdated?: () => void;
}

const formatDateTime = (dateStr: string | null) =>
  formatDate(dateStr, { includeTime: true });

const formatCronExpression = (cron: string | null) => {
  if (!cron) return "Not scheduled";
  
  const parts = cron.split(" ");
  if (parts.length !== 5) return cron;
  
  if (cron === "0 0 * * *") return "Daily at midnight";
  if (cron === "0 */6 * * *") return "Every 6 hours";
  if (cron === "0 9 * * 1-5") return "Weekdays at 9am";
  if (cron === "0 0 * * 0") return "Weekly on Sunday";
  if (cron === "0 0 1 * *") return "Monthly on the 1st";
  const cadence = cadenceForCron(cron);
  if (cadence !== "custom") return `${CADENCE_PRESETS[cadence].label}: ${CADENCE_PRESETS[cadence].when}`;
  
  return cron;
};

const getPhaseLabel = (phase: string | null): { label: string; icon: typeof Search; color: string } => {
  switch (phase) {
    case 'discovery':
      return { label: 'Discovering URLs', icon: Search, color: 'text-blue-500' };
    case 'scraping':
      return { label: 'Scraping Properties', icon: Database, color: 'text-amber-500' };
    case 'completed':
      return { label: 'Completed', icon: CheckCircle, color: 'text-green-500' };
    case 'failed':
      return { label: 'Failed', icon: XCircle, color: 'text-red-500' };
    case 'paused':
      return { label: 'Paused', icon: Pause, color: 'text-gray-500' };
    case 'cancelled':
      return { label: 'Cancelled', icon: XCircle, color: 'text-gray-500' };
    default:
      return { label: 'Pending', icon: Clock, color: 'text-gray-400' };
  }
};

export const JobDetailsDialog = ({ open, onOpenChange, job, onJobUpdated }: JobDetailsDialogProps) => {
  const { canManageJobs } = useAuth();
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [runCountsByRunId, setRunCountsByRunId] = useState<
    Map<string, JobRunSummary>
  >(new Map());
  const [suburbs, setSuburbs] = useState<JobSuburbWithStats[]>([]);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState(false);
  const [showAllSuburbsByJob, setShowAllSuburbsByJob] = useState<Record<string, boolean>>({});
  const [discoveryTasks, setDiscoveryTasks] = useState<DiscoveryTaskInfo[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const loadRuns = useCallback(async () => {
    if (!job?.id) return;
    try {
      setIsLoadingRuns(true);
      const data = await fetchJobRuns(job.id);
      setRuns(data);
      // Batch-load fresh queue counts per run so the History tab doesn't
      // show stale `job_runs.urls_*` columns (Firebase never writes them).
      const counts = await fetchJobRunCountsBatch(job.id);
      setRunCountsByRunId(counts);
      console.log("[JobDetailsDialog/history] loaded run counts", {
        jobId: job.id,
        runs: data.length,
        countsKeys: Array.from(counts.keys()).slice(0, 5),
      });
    } catch (error) {
      console.error('Failed to load job runs:', error);
    } finally {
      setIsLoadingRuns(false);
    }
  }, [job?.id]);

  const [suburbBreakdown, setSuburbBreakdown] = useState<SuburbBreakdown[]>([]);

  const loadSuburbs = useCallback(async () => {
    if (!job?.id) return;
    try {
      setIsLoadingSuburbs(true);
      const data = await fetchJobSuburbs(job.id);
      setSuburbs(data);
    } catch (error) {
      console.error('Failed to load suburbs:', error);
    } finally {
      setIsLoadingSuburbs(false);
    }
  }, [job?.id]);

  // Per-(suburb, adapter) breakdown for the Suburbs tab. Returns both
  // job-total (all runs) and current-run scoped counts so the UI can
  // show "Job total: 1,830 unique URLs / Current run: 412".
  useEffect(() => {
    if (!job?.id) {
      setSuburbBreakdown([]);
      return;
    }
    const runId = runs[0]?.id ?? null;
    let cancelled = false;
    fetchJobSuburbAdapterBreakdown(job.id, runId)
      .then((data) => {
        if (cancelled) return;
        setSuburbBreakdown(data);
      })
      .catch((err) =>
        console.error("[JobDetailsDialog/suburbs] breakdown failed", err),
      );
    return () => {
      cancelled = true;
    };
    // Depend on the latest run id (a stable string) rather than the whole
    // `runs` array — a new array reference on every realtime tick caused
    // this effect to refetch the suburb breakdown and remount the subtree,
    // which manifested as tabs/dialog flickering while a job was running.
  }, [job?.id, runs[0]?.id]);

  const loadDiscoveryTasks = useCallback(async (runId: string) => {
    try {
      const data = await fetchJobDiscoveryTasks(runId);
      setDiscoveryTasks(data);
    } catch (error) {
      console.error('Failed to load discovery tasks:', error);
    }
  }, []);

  // Depend on `jobId` (stable string) not the whole `job` object. The
  // parent JobsTable re-runs setSelectedJob(matched) on every realtime
  // tick — matched is a fresh object ref even when the ID hasn't changed.
  // Tracking `job` directly caused loadRuns/loadSuburbs to refire every
  // few hundred ms while a job was actively scraping, manifesting as the
  // dialog freezing when the user tried to switch tabs.
  const jobId = job?.id ?? null;
  useEffect(() => {
    if (open && jobId) {
      void loadRuns();
      void loadSuburbs();
    }
  }, [open, jobId, loadRuns, loadSuburbs]);

  // Same reason: depend on the latest run id (stable string) rather than
  // the `runs` array reference, which loadRuns rewrites on every refetch.
  const latestRunIdForDiscovery = runs[0]?.id ?? null;
  useEffect(() => {
    if (latestRunIdForDiscovery) {
      void loadDiscoveryTasks(latestRunIdForDiscovery);
    }
  }, [latestRunIdForDiscovery, loadDiscoveryTasks]);

  const handlePause = async (runId: string) => {
    const result = await pauseJobRun(runId);
    if (result.success) {
      toast({ title: "Paused", description: "Job run has been paused" });
      loadRuns();
    } else {
      toast({ title: "Error", description: result.error, variant: "destructive" });
    }
  };

  const handleResume = async (runId: string) => {
    const result = await resumeJobRun(runId);
    if (result.success) {
      toast({ title: "Resumed", description: "Job run has been resumed" });
      loadRuns();
    } else {
      toast({ title: "Error", description: result.error, variant: "destructive" });
    }
  };

  const handleCancel = async (runId: string) => {
    const result = await cancelJobRun(runId);
    if (result.success) {
      toast({ title: "Cancelled", description: "Job run has been cancelled" });
      loadRuns();
    } else {
      toast({ title: "Error", description: result.error, variant: "destructive" });
    }
  };

  // Suburbs display - top 100 by default; "Show All" button covers the rare > 100 case
  const SUBURB_LIMIT = 100;
  const showAllSuburbs = job?.id ? Boolean(showAllSuburbsByJob[job.id]) : false;
  const visibleSuburbs = showAllSuburbs ? suburbs : suburbs.slice(0, SUBURB_LIMIT);
  const hasMoreSuburbs = suburbs.length > SUBURB_LIMIT;

  const toggleShowAllSuburbs = () => {
    if (!job?.id) return;
    setShowAllSuburbsByJob((prev) => ({
      ...prev,
      [job.id]: !showAllSuburbs,
    }));
  };

  // Group suburbs by state for display (must be before early return to obey Rules of Hooks)
  const suburbsByState = useMemo(() => {
    const grouped = new Map<string, JobSuburbWithStats[]>();
    for (const s of visibleSuburbs) {
      const existing = grouped.get(s.state) || [];
      existing.push(s);
      grouped.set(s.state, existing);
    }
    return grouped;
  }, [visibleSuburbs]);

  const config = job?.config as Record<string, unknown> | undefined;
  const latestRun = runs[0];
  const phaseInfo = latestRun ? getPhaseLabel(latestRun.phase) : null;
  // Live counts derived from scrape_queue rows are the ONLY source of truth.
  // We no longer fall back to `latestRun.urls_discovered/scraped` because
  // those cached columns were silently truncated by the pre-fix reconciler's
  // 1000-row PostgREST limit, producing impossible states like "discovered:
  // 0, scraped: 1895". A spinner while the live query loads is better than
  // a contradictory snapshot.
  const latestRunCounts = useJobRunCounts(latestRun?.id ?? null);
  const liveDiscovered = latestRunCounts.total;
  const liveScraped = latestRunCounts.completed;

  if (!job) return null;

  // Calculate progress — uses live counts derived from scrape_queue.
  const getProgress = (): number => {
    if (!latestRun) return 0;
    if (latestRun.phase === 'completed') return 100;
    if (liveDiscovered > 0) return (liveScraped / liveDiscovered) * 100;
    if (latestRun.phase === 'discovery') return -1;
    return 0;
  };

  // Get adapter configs from job relations (safely)
  const jobAdaptersRaw = job.job_adapters;
  const adapterConfigs = jobAdaptersRaw as Array<{
    id: string;
    config: Record<string, unknown>;
    adapters: { name: string; slug: string } | null;
  }> | undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {job.name}
            <Badge variant="outline" className="text-xs font-mono">
              {job.id.slice(0, 8)}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Job details, suburbs, execution history, and logs
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="suburbs">
              Suburbs
              {suburbs.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">
                  {suburbs.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>

          {/* ================================================== */}
          {/* OVERVIEW TAB */}
          {/* ================================================== */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge className={job.status === 'running' ? 'status-running' : job.status === 'scheduled' ? 'status-pending' : ''}>
                  {job.status}
                </Badge>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Run Type</p>
                <p className="text-sm capitalize">{job.run_type}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Schedule</p>
                <p className="text-sm font-mono">{formatCronExpression(job.cron_expression)}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Last Run</p>
                <p className="text-sm">{formatDateTime(job.last_run_at)}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Next Run</p>
                <p className="text-sm">{formatDateTime(job.next_run_at)}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Suburbs</p>
                <p className="text-sm">{suburbs.length} suburb{suburbs.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {canManageJobs && (
              <JobScheduleCard
                key={`${job.id}:${job.updated_at}:${(job.job_suburb_lists ?? []).map((row) => row.suburb_list_id).join(",")}`}
                job={job}
                onSaved={onJobUpdated}
              />
            )}

            {/* Queue Stats — live counts for the latest run only (not job
                aggregate). job-wide totals were confusing because they
                summed across every historical run of this job. */}
            {latestRun && latestRunCounts.total > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Latest Run Queue</p>
                  <span className="text-xs text-muted-foreground font-mono">
                    {latestRun.id.slice(0, 8)}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-2 rounded bg-muted">
                    <p className="text-lg font-bold">{latestRunCounts.total}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="p-2 rounded bg-yellow-50 dark:bg-yellow-900/20">
                    <p className="text-lg font-bold text-yellow-600">{latestRunCounts.pending}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="p-2 rounded bg-green-50 dark:bg-green-900/20">
                    <p className="text-lg font-bold text-green-600">{latestRunCounts.completed}</p>
                    <p className="text-xs text-muted-foreground">Done</p>
                  </div>
                  <div className="p-2 rounded bg-red-50 dark:bg-red-900/20">
                    <p className="text-lg font-bold text-red-600">{latestRunCounts.failed}</p>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                </div>
                {latestRunCounts.total > 0 && (
                  <div className="space-y-1 pt-2">
                    <Progress
                      value={(latestRunCounts.completed / latestRunCounts.total) * 100}
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      {Math.round((latestRunCounts.completed / latestRunCounts.total) * 100)}% complete
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Latest Run with Phase */}
            {latestRun && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Latest Run</p>
                  {latestRun.status === 'running' && (
                    <div className="flex items-center gap-2">
                      {latestRun.phase === 'paused' ? (
                        <Button size="sm" variant="outline" onClick={() => handleResume(latestRun.id)}>
                          <Play className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handlePause(latestRun.id)}>
                          <Pause className="w-4 h-4 mr-1" />
                          Pause
                        </Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => handleCancel(latestRun.id)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>

                {/* Phase indicator */}
                {phaseInfo && latestRun.status === 'running' && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <phaseInfo.icon className={`w-5 h-5 ${phaseInfo.color} ${latestRun.phase !== 'paused' && latestRun.phase !== 'completed' && latestRun.phase !== 'failed' ? 'animate-pulse' : ''}`} />
                    <div className="flex-1">
                      <p className="font-medium">{phaseInfo.label}</p>
                      {latestRun.phase === 'discovery' && (
                        <p className="text-sm text-muted-foreground">
                          {liveDiscovered} queue rows so far
                        </p>
                      )}
                      {latestRun.phase === 'scraping' && (
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {liveScraped} / {liveDiscovered} scraped
                          </p>
                          <Progress value={getProgress()} className="h-2" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <Badge className={latestRun.status === 'completed' ? 'status-completed' : latestRun.status === 'failed' ? 'status-failed' : 'status-running'}>
                      {latestRun.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Started</p>
                    <p>{formatDateTime(latestRun.started_at)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Queue rows</p>
                    <p>{liveDiscovered}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Scraped</p>
                    <p>{liveScraped}</p>
                  </div>
                  {latestRun.completed_at && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Completed</p>
                      <p>{formatDateTime(latestRun.completed_at)}</p>
                    </div>
                  )}
                  {latestRun.error_message && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Error</p>
                      <p className="text-sm text-red-500">{latestRun.error_message}</p>
                    </div>
                  )}
                  {latestRun.output_file_path && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground mb-2">Output File</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/dashboard/exports')}
                      >
                        <FileJson className="w-4 h-4 mr-2" />
                        View in Exports
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Discovery Tasks: one row per (adapter, suburb, listing type) chain. */}
                {discoveryTasks.length > 0 && (
                  <div className="pt-3 border-t">
                    <DiscoveryTasksPanel tasks={discoveryTasks} />
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ================================================== */}
          {/* SUBURBS TAB */}
          {/* ================================================== */}
          <TabsContent value="suburbs" className="space-y-4">
            {isLoadingSuburbs ? (
              <div className="py-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : suburbs.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <MapPin className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No suburbs configured</p>
                <p className="text-sm">This job doesn't have any suburbs linked</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <p className="text-sm text-muted-foreground">
                      Showing {visibleSuburbs.length} of {suburbs.length} suburbs
                      {!showAllSuburbs && hasMoreSuburbs && ` (top ${SUBURB_LIMIT})`}
                    </p>
                    {hasMoreSuburbs && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={toggleShowAllSuburbs}
                      >
                        {showAllSuburbs ? `Show Top ${SUBURB_LIMIT}` : `Show All ${suburbs.length}`}
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium">Total URLs:</span>
                    <span>{suburbs.reduce((sum, s) => sum + s.urls_discovered, 0)} discovered</span>
                    <span>/</span>
                    <span className="text-green-600">{suburbs.reduce((sum, s) => sum + s.urls_scraped, 0)} scraped</span>
                    {suburbs.reduce((sum, s) => sum + s.urls_failed, 0) > 0 && (
                      <>
                        <span>/</span>
                        <span className="text-red-600">{suburbs.reduce((sum, s) => sum + s.urls_failed, 0)} failed</span>
                      </>
                    )}
                  </div>
                </div>

                <ScrollArea className="h-[480px]">
                  {Array.from(suburbsByState.entries()).map(([state, stateSuburbs]) => (
                    <div key={state} className="mb-4">
                      <div className="flex items-center gap-2 mb-2 sticky top-0 bg-background py-1">
                        <Badge variant="secondary" className="text-xs font-bold">
                          {state}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {stateSuburbs.length} suburb{stateSuburbs.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {stateSuburbs.map((suburb) => {
                          const breakdown = suburbBreakdown.find((b) => b.suburb_id === suburb.suburb_id);
                          return (
                            <SuburbBreakdownCard
                              key={suburb.suburb_id}
                              suburb={suburb}
                              breakdown={breakdown}
                              hasCurrentRun={Boolean(runs[0]?.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </ScrollArea>

                {hasMoreSuburbs && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={toggleShowAllSuburbs}
                  >
                    {showAllSuburbs ? (
                      <>
                        <ChevronRight className="w-4 h-4 mr-1" />
                        Show Top {SUBURB_LIMIT} Only
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-1" />
                        Show All {suburbs.length} Suburbs
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
          </TabsContent>

          {/* ================================================== */}
          {/* CONFIG TAB (Backend Details) */}
          {/* ================================================== */}
          <TabsContent value="config" className="space-y-4">
            {/* Job-level config */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Code className="w-4 h-4" />
                Job Config
              </p>
              <pre className="text-xs p-3 rounded-lg bg-muted overflow-x-auto font-mono">
                {JSON.stringify(config, null, 2)}
              </pre>
            </div>

            {/* Per-adapter configs */}
            {adapterConfigs && adapterConfigs.length > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Plug className="w-4 h-4" />
                  Adapter Configs ({adapterConfigs.length})
                </p>
                {adapterConfigs.map((ja) => {
                  const slug = ja.adapters?.slug;
                  // First suburb (alphabetical by name) — gives the
                  // preview button a concrete suburb to seed with.
                  const firstSuburb = suburbs[0];
                  const previewHref = slug && firstSuburb
                    ? `/dashboard/adapters/${encodeURIComponent(slug)}?locality=${encodeURIComponent(firstSuburb.suburb_name)}&postcode=${encodeURIComponent(firstSuburb.postcode)}&state=${encodeURIComponent(firstSuburb.state)}&maxPages=3&config=${encodeURIComponent(JSON.stringify(ja.config))}`
                    : null;
                  return (
                    <div key={ja.id} className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs font-mono">
                            {slug || 'unknown'}
                          </Badge>
                          <span className="text-sm">{ja.adapters?.name || 'Unknown Adapter'}</span>
                        </div>
                        {previewHref && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => navigate(previewHref)}
                          >
                            <Globe className="w-3 h-3 mr-1" />
                            Preview discovery
                          </Button>
                        )}
                      </div>
                      <pre className="text-xs p-3 rounded-lg bg-muted overflow-x-auto font-mono">
                        {JSON.stringify(ja.config, null, 2)}
                      </pre>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Discovery task search URLs */}
            {discoveryTasks.length > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Search URLs (Discovery Tasks)
                </p>
                <div className="space-y-2">
                  {discoveryTasks.map((dt) => (
                    <div key={dt.id} className="text-xs p-2 rounded bg-muted/30 space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs px-1.5">
                          {dt.state}
                        </Badge>
                        <span className="font-medium">{dt.suburb_name}</span>
                      </div>
                      {dt.search_url && (
                        <p className="font-mono text-muted-foreground break-all">
                          {dt.search_url}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Internal IDs */}
            <div className="space-y-2 pt-4 border-t">
              <p className="text-sm font-medium flex items-center gap-2">
                <Info className="w-4 h-4" />
                Internal Details
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Job ID:</span>
                  <span className="ml-2 font-mono">{job.id}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created:</span>
                  <span className="ml-2">{formatDateTime(job.created_at)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated:</span>
                  <span className="ml-2">{formatDateTime(job.updated_at)}</span>
                </div>
                {latestRun && (
                  <div>
                    <span className="text-muted-foreground">Latest Run ID:</span>
                    <span className="ml-2 font-mono">{latestRun.id.slice(0, 8)}</span>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ================================================== */}
          {/* RUN HISTORY TAB */}
          {/* ================================================== */}
          <TabsContent value="history" className="space-y-4">
            {isLoadingRuns ? (
              <div className="py-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : runs.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No run history</p>
                <p className="text-sm">This job hasn't been executed yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Phase</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Scraped / Queue rows</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Out of range</TableHead>
                    <TableHead>Skipped (held)</TableHead>
                    <TableHead>Run ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => {
                    const runPhaseInfo = getPhaseLabel(run.phase);
                    // Live counts derived from scrape_queue. When the
                    // queue has no rows for a run id, `live` is undefined
                    // — render "—" so the user can tell "no queue data"
                    // apart from "really zero".
                    const live = runCountsByRunId.get(run.id);
                    const discovered = live?.total ?? run.urls_discovered ?? 0;
                    const scraped = live?.completed ?? run.urls_scraped ?? 0;
                    const failedRaw = live?.failed;
                    const outOfRangeRaw = live?.outOfRange;
                    const skippedTotal = live
                      ? Object.values(live.skipped).reduce((sum, n) => sum + n, 0)
                      : undefined;
                    const skippedSplit = live ? describeDedupeSkips(live.skipped) : "";
                    const handleRowClick = () =>
                      navigate(`/dashboard/jobs/${job.id}/runs/${run.id}`);

                    return (
                      <TableRow
                        key={run.id}
                        className="cursor-pointer"
                        onClick={handleRowClick}
                      >
                        <TableCell>
                          <Badge className={
                            run.status === 'completed' ? 'status-completed' :
                            run.status === 'failed' ? 'status-failed' :
                            run.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                            'status-running'
                          }>
                            {run.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {run.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                            {run.status === 'running' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                            {run.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={`flex items-center gap-1 text-sm ${runPhaseInfo.color}`}>
                            <runPhaseInfo.icon className="w-3 h-3" />
                            {runPhaseInfo.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{formatDateTime(run.started_at)}</TableCell>
                        <TableCell className="text-sm font-mono">
                          {scraped.toLocaleString()} / {discovered.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {failedRaw === undefined ? (
                            <span className="text-muted-foreground">—</span>
                          ) : failedRaw > 0 ? (
                            <span className="text-red-600">{failedRaw.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {outOfRangeRaw === undefined ? (
                            <span className="text-muted-foreground">—</span>
                          ) : outOfRangeRaw > 0 ? (
                            <span className="text-amber-600">{outOfRangeRaw.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-mono" title={skippedSplit || undefined}>
                          {skippedTotal === undefined ? (
                            <span className="text-muted-foreground">—</span>
                          ) : skippedTotal > 0 ? (
                            <span>
                              {skippedTotal.toLocaleString()}
                              <span className="block text-[10px] text-muted-foreground font-sans">{skippedSplit}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">
                          {run.id.slice(0, 8)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          {/* ================================================== */}
          {/* CRAWL LOGS TAB */}
          {/* ================================================== */}
          <TabsContent value="logs" className="space-y-4">
            <RunLogsPanel
              jobRunId={latestRun?.id ?? null}
              adapterOptions={Array.from(
                new Set(discoveryTasks.map((dt) => dt.adapter_slug).filter(Boolean)),
              ).sort()}
              suburbOptions={Array.from(
                new Set(suburbs.map((s) => s.suburb_name).filter(Boolean)),
              ).sort()}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

interface SuburbBreakdownCardProps {
  suburb: JobSuburbWithStats;
  breakdown: SuburbBreakdown | undefined;
  hasCurrentRun: boolean;
}

function formatScopeLine(scope: {
  uniqueUrls: number;
  scraped: number;
  failed: number;
  outOfRange: number;
}): string {
  if (scope.uniqueUrls === 0) return "0 unique URLs";
  const pct = Math.round((scope.scraped / scope.uniqueUrls) * 100);
  const parts = [
    `${scope.uniqueUrls.toLocaleString()} unique URLs`,
    `${scope.scraped.toLocaleString()} scraped (${pct}%)`,
  ];
  if (scope.failed > 0) parts.push(`${scope.failed.toLocaleString()} failed`);
  if (scope.outOfRange > 0) parts.push(`${scope.outOfRange.toLocaleString()} out of range`);
  return parts.join(" · ");
}

function SuburbBreakdownCard({ suburb, breakdown, hasCurrentRun }: SuburbBreakdownCardProps) {
  const jobScope = breakdown?.jobTotal;
  const runScope = breakdown?.runTotal;
  return (
    <div className="rounded border p-3 space-y-2 hover:bg-muted/30 transition-colors">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="font-medium">{suburb.suburb_name}</span>
          <span className="text-xs text-muted-foreground">{suburb.postcode}</span>
          {suburb.region && (
            <span className="text-xs text-muted-foreground">({suburb.region})</span>
          )}
        </div>
      </div>
      {/* Two-line per-suburb summary: cumulative across runs + current-run. */}
      <div className="text-xs text-muted-foreground space-y-0.5 pl-5">
        <div>
          <span className="font-medium text-foreground">Job total</span>{" "}
          (all runs):{" "}
          {jobScope ? formatScopeLine(jobScope) : "no data"}
        </div>
        <div>
          <span className="font-medium text-foreground">Current run</span>:{" "}
          {!hasCurrentRun
            ? "no run yet"
            : runScope
              ? formatScopeLine(runScope)
              : "no data"}
        </div>
      </div>
      {breakdown && breakdown.adapters.length > 0 && (
        <div className="border-t pt-2 mt-1 pl-5 space-y-1.5">
          {breakdown.adapters.map((adapter) => (
            <div key={adapter.adapter_id} className="text-xs space-y-0.5">
              <div className="font-mono text-foreground">{adapter.adapter_slug}</div>
              <div className="text-muted-foreground pl-3">
                Job: {formatScopeLine(adapter.jobTotal)}
              </div>
              {hasCurrentRun && (
                <div className="text-muted-foreground pl-3">
                  Run: {formatScopeLine(adapter.runTotal)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

